import {
  everyTrue,
  isAddressInMoveOut,
  isAddressInRelocation,
  isContractStatus,
  isMoveOutOnlineNotPossible,
  isRelocationChangeOnlineNotPossible,
} from '@mwe/utils';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PRODUCTS_DETAILS_ROUTE, PRODUCTS_ROUTE } from '@mwe/constants';
import { BadgeType, ContractStatusEnum, Product, UnpaidInvoices } from '@mwe/models';
import { ProductDetailsStateService, ProductsWhiteListLogic, RelocationService } from '@mwe/services';
import { BasicCardClickableComponent } from '../../cards/card-clickable/basic-card-clickable.component';
import { BadgeComponent } from '../../badge/badge/badge.component';
import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { CorrelatedCompanyReferencePipe, TranslateCategoryPipe, TranslateTariffClassPipe } from '@mwe/ui/pipes';
import { ProductIconsComponent } from '../../product-icons/product-icons.component';

@Component({
  selector: 'mwe-product-card',
  templateUrl: './products-card.component.html',
  imports: [
    BasicCardClickableComponent,
    BadgeComponent,
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    TranslateDirective,
    AsyncPipe,
    TranslatePipe,
    TranslateCategoryPipe,
    CorrelatedCompanyReferencePipe,
    TranslateTariffClassPipe,
    ProductIconsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductsCardComponent implements OnInit {
  private readonly productDetailsStateService = inject(ProductDetailsStateService);
  private readonly productsWhiteList = inject(ProductsWhiteListLogic);
  private readonly relocationService = inject(RelocationService);
  private readonly router = inject(Router);

  readonly groupProducts = input<Product[]>(undefined);
  readonly state = input<'details' | 'list'>(undefined);
  readonly showCompanyCorrelation = input<boolean>(undefined);
  readonly checkIfFernwaermeIsActive = input<boolean>(false);
  readonly unpaidInvoices = input<UnpaidInvoices>(undefined);
  readonly useClickableCardStyle = input<boolean>(false);

  readonly iconExtendedStyle = model<string>();
  readonly product = model<Product>();

  private isRelocationOnlineNotPossible = false;
  private isMoveOutOnlineNotPossible = false;
  onlineNotPossibleWarningLabel: string;
  buttonIsFocused = false;

  readonly isActive = computed(() => this.productsWhiteList.isInWhiteList(this.product()?.category));
  readonly hasContractEnded = computed<boolean>(() => isContractStatus(this.product().vertragsStatus, ContractStatusEnum.Beendet));
  readonly contractEndedBadgeTyp = computed<BadgeType>(() => (this.state() === 'details' ? 'warning' : 'neutral'));
  readonly isClickable = computed(() => this.isActive() && this.isDetailsState());
  readonly isDetailsState = computed(() => this.state() === 'details');
  readonly isActiveBadgeVisible = computed<boolean>(
    () => isContractStatus(this.product().vertragsStatus, ContractStatusEnum.Aktiv) && this.state() === 'list',
  );
  readonly categoryIcon = computed(() => {
    const category = this.product().category.toLowerCase();
    if (this.iconExtendedStyle() && this.iconExtendedStyle().length > 0) {
      return `${category}-${this.iconExtendedStyle()}`;
    }

    return category;
  });

  async ngOnInit(): Promise<void> {
    await this.setNoOnlineCategory();

    if (!this.isActive && !this.iconExtendedStyle()) {
      this.iconExtendedStyle.set('grau');
    }
  }

  showProductDetails(event): void {
    event.preventDefault();
    this.productDetailsStateService.product = this.product();
    this.productDetailsStateService.groupProducts = this.groupProducts();
    this.router.navigateByUrl(`/${PRODUCTS_ROUTE}/${PRODUCTS_DETAILS_ROUTE}`);
  }

  async setNoOnlineCategory(): Promise<void> {
    const isRelocation = isAddressInRelocation(this.product().address);
    if (everyTrue(isRelocation, isRelocationChangeOnlineNotPossible(this.product().category))) {
      try {
        const relocationData = await this.relocationService.getParsedRelocationSummaryDetailsFor(this.product().address.addressGroup.id);
        this.isRelocationOnlineNotPossible = relocationData.noOnlineCategories.includes(this.product().category);
      } catch {
        // REVIEW !!! is this the correct default behaviour ??
        this.isRelocationOnlineNotPossible = true;
      }
    }

    this.isMoveOutOnlineNotPossible = isAddressInMoveOut(this.product().address) && isMoveOutOnlineNotPossible(this.product().category);

    if (this.isMoveOutOnlineNotPossible || this.isRelocationOnlineNotPossible) {
      this.iconExtendedStyle.set('');

      this.product.update(old => ({
        ...old,
        isAddressDetailsLinkOnlineNotPossible: true,
      }));
      this.onlineNotPossibleWarningLabel = this.isMoveOutOnlineNotPossible
        ? 'products.card.moveOutActive'
        : 'products.card.relocationActive';
    }
  }
}
